import {styled} from "@mui/material/styles";
import {Box, Button, Stack} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {Doughnut} from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js";
import {fontFamilyPrimaryTheme} from "../../../config/themes/theme";
import {ISolvesInfo} from "../api/parseGetSolvesInfoResponse";

ChartJS.register(ArcElement, Tooltip, Legend);

const StyledButtonText = styled("span")(({theme}) => ({
  color: theme.palette.text.primary,
  fontSize: "0.75rem",
  textTransform: "none",
}));

interface PieChartUIProps {
  radius?: number;
  value?: ISolvesInfo;
}

function PieChartUI({radius = 70, value}: PieChartUIProps) {
  const size = {
    width: 2 * radius,
    height: 2 * radius,
  };

  // Data for Side Label
  const labelData = [
    {
      label: "Easy",
      value: value?.easy ?? "--",
      backgroundColor: "success.light",
      color: "success.main",
    },
    {
      label: "Medium",
      value: value?.medium ?? "--",
      backgroundColor: "warning.light",
      color: "warning.main",
    },
    {
      label: "Hard",
      value: value?.hard ?? "--",
      backgroundColor: "error.light",
      color: "error.main",
    },
  ];

  // Data for Pie Chart
  const chartData = value
    ? {
        labels: ["Easy", "Medium", "Hard"],
        data: [value.easy ?? 0, value.medium ?? 0, value.hard ?? 0],
        backgroundColor: [
          "rgba(12, 189, 128, 0.8)",
          "rgba(255, 184, 0, 0.8)",
          "rgba(235, 87, 87, 0.8)",
        ],
        borderWidth: 3,
        borderRadius: 4,
      }
    : {
        labels: ["Unavailable"],
        data: [0.01],
        backgroundColor: ["#46425520"],
        borderWidth: 0,
        borderRadius: 0,
      };

  const options: ChartOptions<"doughnut"> = {
    cutout: "70%",
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            const label = context[0].label;
            if (value === undefined) {
              return "";
            }
            return label;
          },
          label: function (context) {
            const label = context.label || "";
            if (value === undefined) {
              return "Login to see data";
            }
            const parsedContext = context.parsed;
            return `${label}: ${parsedContext}`;
          },
        },
      },
    },
  };

  const centerLabelText =
    value !== undefined
      ? chartData.data.reduce((acc, curr) => acc + curr, 0)
      : "--";
  const centerLabelPlugin: Plugin<"doughnut"> = {
    id: "centerLabelPlugin",
    beforeDraw: chart => {
      const {width} = chart;
      const {ctx} = chart;
      const centerX = width / 2;
      const centerY =
        chart.chartArea.top +
        (chart.chartArea.bottom - chart.chartArea.top) / 1.9;

      ctx.save();
      ctx.font = `600 28px ${fontFamilyPrimaryTheme}`;
      ctx.fillStyle = "#464255B3";
      ctx.textAlign = "center";

      ctx.fillText(`${centerLabelText}`, centerX, centerY - 5);

      ctx.font = `300 14px ${fontFamilyPrimaryTheme}`;
      ctx.fillText("questions", centerX, centerY + 18);
      ctx.restore();
    },
  };

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid
        size={{xs: 12, sm: 6}}
        container
        justifyContent="center"
        alignItems="center">
        <Box
          sx={{
            width: size.width,
            height: size.height,
            margin: {xs: "0 auto", sm: 0},
          }}>
          <Doughnut
            key={`solves-chart-${value?.easy}-${value?.medium}-${value?.hard}`}
            data={{
              labels: chartData.labels,
              datasets: [
                {
                  data: chartData.data,
                  backgroundColor: chartData.backgroundColor,
                  borderWidth: chartData.borderWidth,
                  borderColor: "white",
                  borderRadius: chartData.borderRadius,
                },
              ],
            }}
            options={options}
            plugins={[centerLabelPlugin]}
          />
        </Box>
      </Grid>

      <Grid
        size={{xs: 12, sm: 6}}
        container
        justifyContent="center"
        alignItems="center">
        <Stack spacing={1}>
          {labelData.map(item => (
            <TotalSolveLabelUI key={item.label} {...item} />
          ))}
        </Stack>
      </Grid>
    </Grid>
  );
}

function TotalSolveLabelUI(stat: {
  label: string;
  value: string | number;
  backgroundColor: string;
  color: string;
}) {
  return (
    <Button
      key={stat.label}
      variant="contained"
      disableElevation
      sx={{
        display: "flex",
        justifyContent: "space-between",
        backgroundColor: stat.backgroundColor,
        "&:hover": {backgroundColor: stat.backgroundColor},
        borderRadius: "8px",
        textTransform: "none",
        padding: "6px 12px",
        minWidth: 140,
      }}>
      <StyledButtonText
        sx={{
          color: stat.color,
          fontSize: "0.875rem",
          fontWeight: 400,
          letterSpacing: "1px",
        }}>
        {stat.label}
      </StyledButtonText>
      <StyledButtonText
        sx={{
          color: stat.color,
          fontSize: "0.875rem",
          fontWeight: 500,
        }}>
        {stat.value}
      </StyledButtonText>
    </Button>
  );
}

export default PieChartUI;
